import React, { useEffect, useState } from 'react';
import { Box, Button, Heading, Label, Paragraph, Textarea } from 'theme-ui';
import Modal from './Modal';
import Alert from './Alert';
import Loading from './Loading';
import { ModalDismiss, BulkRulesBtn } from './Buttons';
import DeliveryRulesEditor, {
  DEFAULT_LEAD_TIME,
  DEFAULT_ORDER_DEADLINE,
  NUMBER_OF_DELIVERY_DAY_RULES_FOR_DISPLAY,
} from './ZipcodeModal/DeliveryRulesEditor';
import DeliveryWindowEditor from './ZipcodeModal/DeliveryWindowEditor';
import { DeliveryRule, DeliveryWindow } from '../state/zipcode/types';
import { useMutationBulkUpsertZipcodeDeliveryRulesMutation } from '../generated/graphql-operations';
import { useAppDispatch, useAppSelector } from '../hooks/store';
import { appSliceActions } from '../state/zipcode';
import airbrake from '../services/airbrake';

const ZIP_REGEX = /^[0-9]{5}$/;
const TIME_REGEX = /^(0[0-9]|1[0-9]|2[0-3]):([0-5][0-9])$/;

interface ApplyResult {
  applied: string[];
  failed: { zipcode: string; message: string }[];
  invalid: string[];
  mutationError: string | null;
}

const emptyRuleTemplate = (): (DeliveryRule | null)[] =>
  new Array(NUMBER_OF_DELIVERY_DAY_RULES_FOR_DISPLAY).fill(null);

/**
 * Applies one delivery-rule template (per-weekday rules + optional delivery
 * window) to a group of zip codes at once, with overwrite semantics. The group
 * is provided as a pasted list of zips; the same DeliveryRulesEditor /
 * DeliveryWindowEditor used for single-zip editing back the template here.
 */
const BulkDeliveryRules = () => {
  const [showModal, setShowModal] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [result, setResult] = useState<ApplyResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [zipsInput, setZipsInput] = useState('');
  const [deliveryRuleData, setDeliveryRuleData] = useState<
    (DeliveryRule | null)[]
  >(emptyRuleTemplate());
  const [deliveryWindowData, setDeliveryWindowData] = useState<DeliveryWindow>({
    startTime: '',
    endTime: '',
  });

  const dispatch = useAppDispatch();
  const bulkRulesPrefill = useAppSelector(
    (state) => state.zipCode.bulkRulesPrefill,
  );

  // When a map selection is handed off ("Edit rules"), open the modal seeded
  // with the selected zips so they flow through the existing parse/apply path.
  useEffect(() => {
    if (bulkRulesPrefill) {
      setZipsInput(bulkRulesPrefill.join(', '));
      setShowModal(true);
    }
  }, [bulkRulesPrefill]);

  const [, bulkUpsertDeliveryRules] =
    useMutationBulkUpsertZipcodeDeliveryRulesMutation();

  const resetState = () => {
    setShowLoading(false);
    setResult(null);
    setErrorMessage(null);
    setZipsInput('');
    setDeliveryRuleData(emptyRuleTemplate());
    setDeliveryWindowData({ startTime: '', endTime: '' });
  };

  const closeModal = () => {
    resetState();
    setShowModal(false);
    dispatch(appSliceActions.clearBulkRulesPrefill());
  };

  const handleToggleDay = (deliveryDay: number, checked: boolean) => {
    setDeliveryRuleData((prev) => {
      const next = [...prev];
      next[deliveryDay] = checked
        ? {
            deliveryDay,
            leadTime: DEFAULT_LEAD_TIME,
            orderDeadline: DEFAULT_ORDER_DEADLINE,
          }
        : null;
      return next;
    });
  };

  const handleLeadTimeChange = (deliveryDay: number, value: string) => {
    setDeliveryRuleData((prev) => {
      const next = [...prev];
      const rule = next[deliveryDay];
      if (rule) {
        next[deliveryDay] = { ...rule, leadTime: parseInt(value, 10) };
      }
      return next;
    });
  };

  const handleOrderDeadlineChange = (deliveryDay: number, value: string) => {
    setDeliveryRuleData((prev) => {
      const next = [...prev];
      const rule = next[deliveryDay];
      if (rule) {
        next[deliveryDay] = { ...rule, orderDeadline: value };
      }
      return next;
    });
  };

  const handleWindowAdjustment = (startTime: string, endTime: string) => {
    setDeliveryWindowData({ startTime, endTime });
  };

  const applyRules = async () => {
    const providedZips = zipsInput
      .split(/[,\s]+/)
      .map((zip) => zip.trim())
      .filter((zip) => zip.length > 0);
    const uniqueZips = Array.from(new Set(providedZips));
    const validZips = uniqueZips.filter((zip) => ZIP_REGEX.test(zip));
    const invalidZips = uniqueZips.filter((zip) => !ZIP_REGEX.test(zip));

    if (validZips.length === 0) {
      setErrorMessage('Please enter at least one valid 5-digit zip code.');
      return;
    }

    const deliveryRules = deliveryRuleData
      .filter((rule): rule is DeliveryRule => rule !== null)
      .map((rule) => ({
        deliveryDay: rule.deliveryDay,
        leadTime: rule.leadTime,
        orderDeadline: rule.orderDeadline,
      }));

    const windowIsValid =
      TIME_REGEX.test(deliveryWindowData.startTime) &&
      TIME_REGEX.test(deliveryWindowData.endTime);
    const deliveryWindow = windowIsValid
      ? {
          startTime: deliveryWindowData.startTime,
          endTime: deliveryWindowData.endTime,
        }
      : undefined;

    // Overwrite semantics make an empty template a destructive "clear all" — guard
    // against an accidental apply with nothing configured.
    if (deliveryRules.length === 0 && !deliveryWindow) {
      setErrorMessage(
        'Enable at least one delivery day or set a delivery window before applying.',
      );
      return;
    }

    setErrorMessage(null);
    setShowLoading(true);

    const { data, error } = await bulkUpsertDeliveryRules({
      input: { zipcodes: validZips, deliveryRules, deliveryWindow },
    });

    setShowLoading(false);

    const payload = data?.bulkUpsertZipcodeDeliveryRules;
    if (error || !payload) {
      airbrake.notify({
        error:
          'error response received from graphql api BulkUpsertZipcodeDeliveryRules',
        params: { error, validZips },
      });
      setErrorMessage(
        `We're sorry, there was an issue and your changes have not been saved. ${
          error?.message ?? ''
        }`,
      );
      return;
    }

    const appliedZips = payload.results
      .filter((r) => r.success)
      .map((r) => r.zipcode);

    // Keep Redux in step with the backend so the saved list and map highlight
    // reflect the new rules without a page refresh (overwrite semantics).
    if (appliedZips.length > 0) {
      dispatch(
        appSliceActions.setDeliveryRulesForZips({
          zipCodes: appliedZips,
          deliveryDays: deliveryRules.map((rule) => rule.deliveryDay),
        }),
      );
      // The handed-off group has been edited — drop it from the map selection.
      dispatch(appSliceActions.clearSelectedZipcodes());
    }

    // Surface any top-level (non per-zip) error alongside the per-zip results
    // rather than instead of them, so partial successes are never dropped.
    setResult({
      applied: appliedZips,
      failed: payload.results
        .filter((r) => !r.success)
        .map((r) => ({
          zipcode: r.zipcode,
          message: r.errors.map((e) => e.message).join(', '),
        })),
      invalid: invalidZips,
      mutationError:
        payload.errors.length > 0
          ? payload.errors.map((e) => e.message).join(' ')
          : null,
    });
  };

  return (
    <>
      <BulkRulesBtn onClick={() => setShowModal(true)} />

      {showModal && (
        <Modal visible={true}>
          <ModalDismiss onClick={closeModal} />
          <Heading variant="text.headingLg" mb={6}>
            {result ? 'Bulk rules result' : 'Bulk delivery rules'}
          </Heading>

          {showLoading ? (
            <Loading text="Applying..." curtain={false} />
          ) : result ? (
            <>
              {result.mutationError && (
                <Alert type="error" text={result.mutationError} />
              )}
              {result.applied.length > 0 && (
                <Alert
                  type="success"
                  label="Rules applied to:"
                  text={result.applied.join(', ')}
                />
              )}
              {result.failed.length > 0 && (
                <Alert
                  type="error"
                  label="Failed to apply:"
                  showCopyIcon={true}
                  text={result.failed
                    .map((f) => `${f.zipcode} (${f.message})`)
                    .join(', ')}
                />
              )}
              {result.invalid.length > 0 && (
                <Alert
                  type="warning"
                  label="Skipped invalid zip codes:"
                  showCopyIcon={true}
                  text={result.invalid.join(', ')}
                />
              )}
              <Box sx={{ textAlign: 'right' }} mt={6}>
                <Button variant="buttons.secondary" onClick={closeModal}>
                  OK
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Paragraph variant="text.labelLg" mb={4}>
                Set a delivery rules template and apply it to every zip code in
                the list. Existing rules for these zip codes are replaced.
              </Paragraph>

              {errorMessage && <Alert type="error" text={errorMessage} />}

              <Label htmlFor="bulkRulesZipCodes" mb={2}>
                Zip codes (5-digit, separated by spaces or commas)
              </Label>
              <Textarea
                id="bulkRulesZipCodes"
                name="bulkRulesZipCodes"
                rows={4}
                value={zipsInput}
                onChange={(e) => setZipsInput(e.target.value)}
              />

              <Heading sx={{ py: 4 }} variant="headingSm">
                Delivery Window
              </Heading>
              <DeliveryWindowEditor
                startTime={deliveryWindowData.startTime}
                endTime={deliveryWindowData.endTime}
                onAdjustment={handleWindowAdjustment}
              />

              <Heading sx={{ py: 4 }} variant="headingSm">
                Delivery Rules
              </Heading>
              <DeliveryRulesEditor
                deliveryRules={deliveryRuleData}
                onToggleDay={handleToggleDay}
                onLeadTimeChange={handleLeadTimeChange}
                onOrderDeadlineChange={handleOrderDeadlineChange}
              />

              <Box sx={{ textAlign: 'right' }} mt={6}>
                <Button variant="buttons.tertiary" onClick={closeModal} mr={2}>
                  Cancel
                </Button>
                <Button variant="buttons.secondary" onClick={applyRules}>
                  Apply
                </Button>
              </Box>
            </>
          )}
        </Modal>
      )}
    </>
  );
};

export default BulkDeliveryRules;
